import { Dialog, DialogTitle, DialogActions, DialogContent, IconButton, Button, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../locales/translations';
import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { SelectedAwarenessCampaignActions } from '../../../store/reducers/awareness_campaigns/selected';
import { showTranslatedMessage } from '../../../store/reducers/snacks';
import AwarenessCampaignsMethods from '../../../actions/awarenessCampaigns';
import { LoadingButton } from '@mui/lab';
import CampaignAddressesMethods from '../../../actions/campaignAddresses';
import { AwarenessCampaignStatus } from '../../../models/AwarenessCampaign';
import { selectNonVisitedAddresses } from '../../../store/reducers/awareness_campaigns/addresses';
import { usePartnerID } from '../../../hooks/partners';

const ConfirmCompleteCampaignDialog: React.FC = () => {
  const { t } = useTranslation([Namespace.DIALOGS, Namespace.ACTIONS]);

  const open = useAppSelector(state => state.awareness_campaign.selected.confirmCompleteDialogOpen);

  const dispatch = useAppDispatch();

  const partnerID = usePartnerID();
  const userEmail = useAppSelector((state) => state.user.email);

  const nonVisitedAddresses = useAppSelector(selectNonVisitedAddresses);

  const [isLoading, setIsLoading] = useState(false);

  const missionID = useAppSelector(state => state.awareness_campaign.selected.selectedAwarenessCampaign?.ID);
  const existingAmbassadorsStatus = useAppSelector(state => state.awareness_campaign.selected.selectedAwarenessCampaign?.ambassadorsStatus);
  
  const handleMarkAsCompleted = async () => {
      if (!userEmail || !partnerID || !missionID) return;

      setIsLoading(true);

      // Bulk update all non-visited addresses as visited
      if (nonVisitedAddresses.length > 0) {
          await dispatch(CampaignAddressesMethods.markAddressesAsVisitedForMissionCompletion(
              partnerID,
              missionID,
              nonVisitedAddresses
          ));
      }

      // Update the ambassador status to completed
      const updatedAmbassadorsStatus = {
          ...existingAmbassadorsStatus,
          [userEmail]: AwarenessCampaignStatus.COMPLETED
      };

      await dispatch(AwarenessCampaignsMethods.updateAwarenessCampaign(
          partnerID,
          missionID,
          { ambassadorsStatus: updatedAmbassadorsStatus }
      ));

      // Show mission completed success message
      dispatch(showTranslatedMessage({
          variant: 'success',
          messageKey: 'mission_marked_completed'
      }));
      setIsLoading(false);
      handleClose();
  };

  const handleClose = () => {
    dispatch(SelectedAwarenessCampaignActions.setConfirmCompleteDialogOpen(false));
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="complete-mission-dialog-title"
    >
      <DialogTitle id="complete-mission-dialog-title">
        {t('confirm_complete_mission', { ns: Namespace.DIALOGS })}
        <IconButton
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1">
          {t('confirm_complete_mission_text', { ns: Namespace.DIALOGS })}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          color="inherit"
        >
          {t('cancel', { ns: Namespace.ACTIONS })}
        </Button>

        <LoadingButton
          onClick={handleMarkAsCompleted}
          color="primary"
          loading={isLoading}
        >
          {t('confirm', { ns: Namespace.ACTIONS })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmCompleteCampaignDialog; 